import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';

import type { FTPServer } from '@sb/integrations/implementations/FTPServer/implementation/FTPServer';
import type { S3Bucket } from '@sb/integrations/implementations/S3Bucket/implementation/S3Bucket';
import type { DeviceCommand } from '@sb/integrations/types';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { LocateResult } from '@sb/routine-runner/vision/LocateTypes';

const log = makeNamespacedLog('captureUtils');

/**
 * Result returned when image validation fails.
 */
export interface ImageValidationFailure {
  failure: { kind: typeof FailureKind.CameraFailure };
  failureReason: string;
}

/**
 * Validates that the image buffer is non-empty and meets a minimum size.
 * Returns a failure result if the image is invalid, or `null` if it is valid.
 */
export function validateImage(
  image: ArrayBuffer,
): ImageValidationFailure | null {
  if (!image || image.byteLength === 0) {
    return {
      failure: { kind: FailureKind.CameraFailure },
      failureReason: 'Received empty image data from camera',
    };
  }

  // Most image formats need at least a few bytes for headers
  if (image.byteLength < 2) {
    return {
      failure: { kind: FailureKind.CameraFailure },
      failureReason: 'Received incomplete image data (too small)',
    };
  }

  return null;
}

/**
 * Converts an image buffer to PNG using sharp and saves it to a temporary file.
 * Returns the path to the temporary file.
 *
 * Throws on format/decoding errors with a descriptive message including
 * `FailureKind.CameraFailure` context, or re-throws unexpected errors.
 */
export async function convertImageToPng(image: ArrayBuffer): Promise<string> {
  const tempFilename = `capture_${Date.now()}_${Math.random().toString(36).substring(7)}.png`;
  const tempFilePath = path.join(os.tmpdir(), tempFilename);

  // `sharp` is loaded lazily, not at module top-level: it's a native addon, and
  // merely importing this module (for `validateImage`, the metadata helpers, the
  // exported types, or any step-schema graph that transitively reaches here) must
  // not trigger the native binary load. Under vitest's worker pool that eager load
  // fails with "Module did not self-register", killing every suite that imports the
  // routine/step graph even though it never converts an image. Deferring the import
  // to the one code path that actually needs sharp keeps those consumers clean.
  const { default: sharp } = await import('sharp');

  try {
    await sharp(image).png().toFile(tempFilePath);
  } catch (error) {
    log.error('convertImageToPng', 'Error converting image with sharp', error);

    if (
      error instanceof Error &&
      (error.message.includes(
        'Input buffer contains unsupported image format',
      ) ||
        error.message.includes('Premature end') ||
        error.message.includes('Vips') ||
        error.message.includes('unsupported') ||
        error.message.includes('corrupt'))
    ) {
      const cameraError = new Error(
        `Image data is corrupted, incomplete, or in an unsupported format: ${error.message}. This may be due to network issues or camera encoding problems.`,
      );

      (cameraError as any).failureKind = FailureKind.CameraFailure;

      throw cameraError;
    }

    throw error;
  }

  log.info('convertImageToPng', 'Converted and saved PNG image to temp file', {
    tempFilePath,
  });

  return tempFilePath;
}

/**
 * Storage equipment type: must expose `kind` and `executeCommand`.
 */
interface StorageEquipment {
  kind: string;
  executeCommand(command: DeviceCommand): Promise<unknown>;
}

/**
 * Uploads a local file to remote storage (FTP or S3).
 */
export async function uploadToStorage(args: {
  tempFilePath: string;
  remotePath: string;
  storageEquipment: StorageEquipment;
}): Promise<void> {
  const { tempFilePath, remotePath, storageEquipment } = args;
  const equipmentKind = storageEquipment.kind;

  let uploadCommand: DeviceCommand;

  if (equipmentKind === 'FTPServer') {
    uploadCommand = {
      kind: 'FTPServerCommand',
      command: {
        command: 'uploadFile',
        localPath: tempFilePath,
        remotePath,
      },
    };
  } else if (equipmentKind === 'S3Bucket') {
    uploadCommand = {
      kind: 'S3BucketCommand',
      command: {
        command: 'uploadFile',
        localPath: tempFilePath,
        remotePath,
      },
    };
  } else {
    throw new Error(`Unsupported storage equipment type: ${equipmentKind}`);
  }

  log.info('uploadToStorage', 'Uploading image to storage', {
    remotePath,
    equipmentKind,
  });

  try {
    if (equipmentKind === 'FTPServer') {
      await (storageEquipment as unknown as FTPServer).executeCommand(
        uploadCommand,
      );
    } else if (equipmentKind === 'S3Bucket') {
      await (storageEquipment as unknown as S3Bucket).executeCommand(
        uploadCommand,
      );
    }
  } catch (uploadError) {
    const errorMessage =
      uploadError instanceof Error
        ? uploadError.message
        : 'Unknown upload error';

    log.error('uploadToStorage', 'Upload failed', {
      error: uploadError,
      remotePath,
      equipmentKind,
    });

    throw new Error(`Failed to upload image to storage: ${errorMessage}`);
  }
}

/**
 * Formats a `Date` into a capture-friendly timestamp string:
 * `YYYY-MM-DD_HH-mm-ss.SSS`
 */
export function formatCaptureTimestamp(date: Date): string {
  const pad = (n: number, w = 2) => n.toString().padStart(w, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}_${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(
    date.getSeconds(),
  )}.${pad(date.getMilliseconds(), 3)}`;
}

/**
 * High-level function that validates an image, converts it to PNG,
 * uploads it to storage, and cleans up. Combines `validateImage`,
 * `convertImageToPng`, and `uploadToStorage` into a single call.
 *
 * Returns an `ImageValidationFailure` if the image is invalid, or `null`
 * on success.
 */
export async function saveImageToStorage(args: {
  image: ArrayBuffer;
  remotePath: string;
  storageEquipment: StorageEquipment;
}): Promise<ImageValidationFailure | null> {
  const { image, remotePath, storageEquipment } = args;

  const validationFailure = validateImage(image);

  if (validationFailure) {
    return validationFailure;
  }

  const tempFilePath = await convertImageToPng(image);

  try {
    await uploadToStorage({
      tempFilePath,
      remotePath,
      storageEquipment,
    });
  } finally {
    await fs.promises.unlink(tempFilePath).catch((unlinkError) => {
      log.warn('saveImageToStorage', 'Failed to delete temporary file', {
        tempFilePath,
        error: unlinkError,
      });
    });
  }

  return null;
}

/**
 * Metadata saved alongside a captured image for Locate and Capture steps.
 */
export interface CaptureMetadata {
  imageFilename: string;
  botId: string;
  botAlias: string | undefined;
  timestamp: string;
  stepKind: 'Locate' | 'Capture' | 'RunLocateSkill';
  /** Set by the RunLocateSkill step to record which saved skill produced the results. */
  skillId?: string;
  locateResults?: LocateResult[];
  stepParameters: Record<string, unknown>;
}

/**
 * Serialises a `CaptureMetadata` object as JSON and uploads it next to the
 * corresponding image.  The JSON path is derived from `remotePath` by
 * replacing the `.png` extension with `_metadata.json`.
 */
export async function saveMetadataToStorage(args: {
  metadata: CaptureMetadata;
  remotePath: string;
  storageEquipment: StorageEquipment;
}): Promise<void> {
  const { metadata, remotePath, storageEquipment } = args;

  const jsonRemotePath = remotePath.replace(/\.png$/i, '_metadata.json');

  const tempFilename = `metadata_${Date.now()}_${Math.random().toString(36).substring(7)}.json`;
  const tempFilePath = path.join(os.tmpdir(), tempFilename);

  await fs.promises.writeFile(
    tempFilePath,
    JSON.stringify(metadata, null, 2),
    'utf-8',
  );

  log.info('saveMetadataToStorage', 'Uploading metadata JSON', {
    jsonRemotePath,
  });

  try {
    await uploadToStorage({
      tempFilePath,
      remotePath: jsonRemotePath,
      storageEquipment,
    });
  } finally {
    await fs.promises.unlink(tempFilePath).catch((unlinkError) => {
      log.warn('saveMetadataToStorage', 'Failed to delete temporary file', {
        tempFilePath,
        error: unlinkError,
      });
    });
  }
}
